import { useState, useEffect } from "react";
import EventCard from "../../Components/Graduado/EventCard";
import { getEvents } from "../../API/getters";
import { Calendar, MapPin, Building2, Mail, Phone, User, Clock, X, CalendarDays, Loader2 } from "lucide-react";

export default function EventPage() {
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const formatDate = (dateString) => {
        if (!dateString) return 'Data a definir';
        const data = new Date(dateString);
        return data.toLocaleString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    }

    useEffect(() => {
        async function fetchEvents() {
            try {
                setLoading(true);
                const events = await getEvents();
                setEvents(events);
            } catch (error) {
                console.error("Erro ao carregar eventos:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchEvents();
    }, []);

    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setIsModalVisible(true);
    };

    const formatSchedule = (schedule) => {
        if (!schedule) return [];
        if (Array.isArray(schedule)) return schedule;
        return schedule.split('#').filter(item => item.trim());
    };

    return (
        <div className="w-full py-6">

            {/* Loading State */}
            {loading && (
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
                    <span className="text-gray-400 text-sm">Carregando eventos...</span>
                </div>
            )}

            {/* Empty State */}
            {!loading && events.length === 0 && (
                <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100">
                    <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                            <CalendarDays className="w-8 h-8 text-emerald-500" />
                        </div>
                    </div>
                    <h3 className="text-lg font-light text-gray-700 mb-2">
                        Não há eventos disponíveis
                    </h3>
                    <p className="text-gray-400 text-sm">
                        Novos eventos serão publicados em breve. Fique atento!
                    </p>
                </div>
            )}

            {/* Events Grid */}
            {!loading && events.length > 0 && (
                <div className="space-y-5">
                    {events.map((event) => (
                        <div 
                            key={event.uuid} 
                            className="cursor-pointer transition-all duration-300 hover:translate-x-1"
                            onClick={() => handleEventClick(event)}
                        >
                            <EventCard
                                title={event.title}
                                date={formatDate(event.date)}
                                location={event.location}
                                image={event.image}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Modal - Custom without Ant Design Modal */}
            {isModalVisible && selectedEvent && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalVisible(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-lg font-medium text-gray-800">
                                Detalhes do Evento
                            </h2>
                            <button
                                onClick={() => setIsModalVisible(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-6">
                            {/* Event Image */}
                            {selectedEvent.image && (
                                <div className="rounded-xl overflow-hidden">
                                    <img
                                        src={selectedEvent.image}
                                        alt={selectedEvent.title}
                                        className="w-full h-56 object-cover"
                                    />
                                </div>
                            )}

                            {/* Event Title */}
                            <h1 className="text-2xl font-light text-gray-800 tracking-tight">
                                {selectedEvent.title}
                            </h1>

                            {/* Event Info Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50/50 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                                        <Calendar className="w-4 h-4 text-emerald-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400">Data</p>
                                        <p className="text-sm text-gray-700">{formatDate(selectedEvent.date)}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                                        <MapPin className="w-4 h-4 text-emerald-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400">Local</p>
                                        <p className="text-sm text-gray-700">{selectedEvent.location || 'Local a definir'}</p>
                                    </div>
                                </div>
                                {selectedEvent.organized_by && (
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                                            <Building2 className="w-4 h-4 text-emerald-500" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400">Organizador</p>
                                            <p className="text-sm text-gray-700">{selectedEvent.organized_by}</p>
                                        </div>
                                    </div>
                                )}
                                {selectedEvent.coordinator && (
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                                            <User className="w-4 h-4 text-emerald-500" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400">Coordenador</p>
                                            <p className="text-sm text-gray-700">{selectedEvent.coordinator}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* About Section */}
                            {selectedEvent.about && (
                                <div>
                                    <h3 className="text-base font-medium text-gray-800 mb-2">Sobre o Evento</h3>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        {selectedEvent.about}
                                    </p>
                                </div>
                            )}

                            {/* Schedule Section */}
                            {selectedEvent.schedule && formatSchedule(selectedEvent.schedule).length > 0 && (
                                <div>
                                    <h3 className="text-base font-medium text-gray-800 mb-3">Programação</h3>
                                    <div className="space-y-2">
                                        {formatSchedule(selectedEvent.schedule).map((item, index) => (
                                            <div key={index} className="flex gap-3 items-start">
                                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center">
                                                    <Clock className="w-3 h-3 text-emerald-500" />
                                                </div>
                                                <p className="text-gray-600 text-sm">{item}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Contact Section */}
                            {(selectedEvent.contact_email || selectedEvent.contact_phone) && (
                                <div>
                                    <h3 className="text-base font-medium text-gray-800 mb-3">Contacto</h3>
                                    <div className="space-y-2">
                                        {selectedEvent.contact_email && (
                                            <div className="flex items-center gap-3">
                                                <Mail className="w-4 h-4 text-gray-400" />
                                                <a href={`mailto:${selectedEvent.contact_email}`} className="text-sm text-emerald-600 hover:text-emerald-700">
                                                    {selectedEvent.contact_email}
                                                </a>
                                            </div>
                                        )}
                                        {selectedEvent.contact_phone && (
                                            <div className="flex items-center gap-3">
                                                <Phone className="w-4 h-4 text-gray-400" />
                                                <a href={`tel:${selectedEvent.contact_phone}`} className="text-sm text-emerald-600 hover:text-emerald-700">
                                                    {selectedEvent.contact_phone}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}